import Tooltip from "@mui/material/Tooltip";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getLayoutOption } from "app/reducers/modalSlice";
import { signOut } from "app/thunks/authAPI";
import logo from "assets/logo/SP.png";
import classNames from "classnames";
import { ConfirmationModal } from "features/confirmation-modal/ConfirmationModal";
import { NAVIGATION_NAME } from "interfaces/Navigation";
import { UserRole } from "interfaces/User";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { LogoutIcon } from "ui";
import { BUTTON_TYPES } from "ui/Button";

import { NavigationContainer } from "../components/NavigationContainer";
import { SideBar } from "../components/side-bar/SideBar";
import { NAVIGATOR, roleBasedRoutes } from "../index";

export const Layout = ({
  role,
  children,
}: {
  role?: UserRole;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hideLayout } = useAppSelector(getLayoutOption);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigation_sections = useMemo(
    () => (role && roleBasedRoutes[role]) || [],
    [role],
  );

  const getCurrentSection = useCallback(() => {
    const section = navigation_sections?.find((nav) =>
      location.pathname.startsWith(nav.href),
    );
    return section ? section.name : NAVIGATION_NAME.DASHBOARD;
  }, [location.pathname, navigation_sections]);

  const [currentSection, setCurrentSection] =
    useState<NAVIGATION_NAME>(getCurrentSection());

  useEffect(() => {
    const currentSection = getCurrentSection();
    setCurrentSection(currentSection);
  }, [getCurrentSection, location.pathname]);

  const openSignOutModal = () => setIsModalOpen(true);

  const handleSignOut = () => {
    dispatch(signOut());
    history.replace(NAVIGATOR.signIn);
    setIsModalOpen(false);
  };

  //TODO: Adapt the logic to show the sidebar when necessary
  const shouldHideSidebar =
    hideLayout ||
    !role ||
    role === UserRole.superAdmin ||
    location.pathname === NAVIGATOR.link ||
    location.pathname === NAVIGATOR.configs ||
    location.pathname === NAVIGATOR.home ||
    location.pathname === NAVIGATOR.devices ||
    location.pathname === NAVIGATOR.matches ||
    location.pathname === NAVIGATOR.documentation;

  return (
    <div className="flex h-screen overflow-hidden">
      <div
        className={
          hideLayout ? "hidden" : "bg-faux-eerie-black flex flex-shrink-0"
        }
      >
        <div className="flex flex-col items-center flex-1 w-24 pb-4 overflow-y-auto px-7">
          <div className="mt-4 mb-auto">
            <div className="flex items-center justify-center rounded-full bg-faux-eerie-blacker w-14 h-14">
              <img alt="Logo" src={logo} height={38} width={38} />
            </div>
          </div>
          <NavigationContainer
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            navigation={navigation_sections}
          />
          <Tooltip title="Sign Out" className="self-end">
            <button
              onClick={openSignOutModal}
              className={classNames(
                "hover:bg-faux-ultramarine-blue hover:bg-opacity-50 hover:text-white text-faux-black-coral",
                "group flex items-center justify-center rounded-md",
              )}
            >
              <LogoutIcon />
            </button>
          </Tooltip>
        </div>
      </div>
      <main
        className={classNames(
          "flex flex-1 relative overflow-y-auto bg-faux-dark-gunmetal focus:outline-none px-2",
          hideLayout && "justify-center",
        )}
      >
        {children}
      </main>
      <div
        className={
          shouldHideSidebar ? "hidden" : "bg-faux-gunmetal flex flex-shrink-0"
        }
      >
        <div className="flex flex-col flex-1 overflow-y-auto w-72">
          <SideBar currentSection={currentSection} />
        </div>
      </div>
      {isModalOpen && (
        <ConfirmationModal
          closeButtonType={BUTTON_TYPES.CANCEL}
          confirmButtonType={BUTTON_TYPES.CONFIRM}
          closeText="Cancelar"
          confirmText="Cerrar sesión"
          title="Cerrar Sesión"
          label="Vas a cerrar sesión, ¿quieres continuar?"
          callback={handleSignOut}
          callbackClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};
